import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { getSupabase } from '@l2u/ui/lib/supabase.ts';
import { setCompanyAccessToken } from '@l2u/ui/lib/auth/companyToken.ts';

type CompanyAuthValue = {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const CompanyAuthContext = createContext<CompanyAuthValue>({
  session: null,
  loading: true,
  signOut: async () => {},
});

export function CompanyAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;
    const supabase = (() => {
      try {
        return getSupabase();
      } catch {
        return null;
      }
    })();

    if (!supabase) {
      setLoading(false);
      return () => {};
    }

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setCompanyAccessToken(data.session?.access_token ?? null);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!mounted) return;
      setSession(next ?? null);
      setCompanyAccessToken(next?.access_token ?? null);
    });
    unsubscribe = () => subscription.subscription.unsubscribe();

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      const supabase = getSupabase();
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setCompanyAccessToken(null);
    }
  }, []);

  const value = useMemo<CompanyAuthValue>(
    () => ({ session, loading, signOut }),
    [session, loading, signOut],
  );

  return <CompanyAuthContext.Provider value={value}>{children}</CompanyAuthContext.Provider>;
}

export function useCompanyAuth(): CompanyAuthValue {
  return useContext(CompanyAuthContext);
}
